import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart,
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { dashboardAPI, googleAdsAPI, campaignsAPI, billingAPI } from '../utils/api';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const REFRESH_MS = 120000;

const FRAUD_TYPE_COLORS = {
  'Over Threshold': '#ff8c42',
  'Fraudulent Device': '#e3b341',
  'Disabled JS': '#7ecbf5',
  'VPN': '#4285f4',
  'Manually Blocked': '#a855f7',
  'Aggressive Approach': '#ec4899',
  'Range Blocking': '#ef4444',
  'Whitelisted': '#9ca3af',
  'Bounced': '#f0b8a8',
  'Out of Geo': '#d4e157',
  'AI': '#10b981',
  'Not Specified': '#c5e8b0',
};
const FRAUD_TYPE_ORDER = Object.keys(FRAUD_TYPE_COLORS);
const PIE_COLORS = Object.values(FRAUD_TYPE_COLORS);

// ── Reusable date range picker ──
function RangePicker({ value, onApply, options }) {
  const [range, setRange] = React.useState(value || '30');
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');

  const presets = options || [
    { v: '7', label: 'Last 7 days' },
    { v: '30', label: 'Last 30 days' },
    { v: '90', label: 'Last 90 days' },
    { v: '365', label: 'Last 12 months' },
    { v: 'custom', label: 'Custom' },
  ];

  const fmtDate = d => d.toISOString().slice(0, 10);
  const fromPreset = (v) => {
    const today = new Date();
    const daysMap = { '7': 7, '30': 30, '90': 90, '365': 365 };
    const d = new Date(today); d.setDate(d.getDate() - (daysMap[v] || 30));
    return { start: fmtDate(d), end: fmtDate(today) };
  };

  const handleRangeChange = (v) => {
    setRange(v);
    if (v !== 'custom') {
      const d = fromPreset(v);
      onApply(d.start, d.end, Number(v) || 30);
    }
  };

  const handleApply = () => {
    if (start && end) {
      const ms = new Date(end) - new Date(start);
      const days = Math.max(1, Math.round(ms / 86400000));
      onApply(start, end, days);
    }
  };

  const inputStyle = { padding: '6px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)', outline: 'none' };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={range} onChange={e => handleRangeChange(e.target.value)} style={inputStyle}>
        {presets.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
      </select>
      {range === 'custom' && (
        <>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} style={inputStyle} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>to</span>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={inputStyle} />
          <button onClick={handleApply} disabled={!start || !end}
            style={{ padding: '6px 14px', background: start && end ? 'var(--accent-blue)' : 'var(--bg-secondary)', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, color: start && end ? '#fff' : 'var(--text-muted)', cursor: start && end ? 'pointer' : 'default' }}>
            Apply
          </button>
        </>
      )}
    </div>
  );
}

// ── Skeleton loading ──

function Sk({ w = '100%', h = 14, r = 7, mb = 0, mt = 0 }) {
  return (
    <div className="skeleton" style={{
      width: w, height: h, borderRadius: r,
      marginBottom: mb || undefined, marginTop: mt || undefined,
      flexShrink: 0,
    }} />
  );
}

function DashboardSkeleton() {
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Sk w={160} h={24} r={6} />
          <Sk w={220} h={12} r={5} />
        </div>
        <Sk w={230} h={38} r={10} />
      </div>

      {/* 6 stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton-card" style={{ animationDelay: `${i * 60}ms` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <Sk w="50%" h={10} r={4} />
              <Sk w={32} h={32} r={8} />
            </div>
            <Sk w="65%" h={28} r={6} mb={10} />
            <Sk w="38%" h={9} r={4} />
          </div>
        ))}
      </div>

      {/* Main chart */}
      <div className="skeleton-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Sk w={180} h={14} r={5} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Sk w={70} h={30} r={8} />
            <Sk w={70} h={30} r={8} />
          </div>
        </div>
        {/* Bar chart skeleton */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 200, paddingTop: 8 }}>
          {[65, 40, 80, 55, 70, 45, 90, 60, 75, 50, 85, 42, 68, 55, 78,
            48, 62, 88, 52, 72, 38, 66, 82, 44].map((h, i) => (
            <div key={i} className="skeleton" style={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0', animationDelay: `${i * 30}ms` }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          {[...Array(6)].map((_, i) => <Sk key={i} w={32} h={9} r={3} />)}
        </div>
      </div>

      {/* Two-col panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="skeleton-panel">
          <Sk w="45%" h={14} r={5} mb={18} />
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Sk w="40%" h={10} r={4} />
                <Sk w="15%" h={10} r={4} />
              </div>
              <Sk w="100%" h={8} r={4} />
            </div>
          ))}
        </div>
        <div className="skeleton-panel">
          <Sk w="40%" h={14} r={5} mb={18} />
          {/* Donut chart skeleton */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div className="skeleton" style={{ width: 120, height: 120, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sk w={10} h={10} r={3} />
                  <Sk w="60%" h={10} r={4} />
                  <Sk w="15%" h={10} r={4} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom panel */}
      <div className="skeleton-panel">
        <Sk w="30%" h={14} r={5} mb={18} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Sk w="50%" h={9} r={4} />
              <Sk w="75%" h={22} r={5} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Transform Google Ads API perf response → gadsData shape ──

function transformGadsPerf(perf) {
  if (!perf || !perf.campaigns || perf.campaigns.length === 0) return null;
  const camps = perf.campaigns;
  const totals = camps.reduce((acc, c) => ({
    clicks: acc.clicks + c.clicks,
    impressions: acc.impressions + c.impressions,
    cost: acc.cost + c.cost,
    conversions: acc.conversions + c.conversions,
  }), { clicks: 0, impressions: 0, cost: 0, conversions: 0 });
  const avg_ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100).toFixed(2) : '0.00';
  const avg_cpc = totals.clicks > 0 ? (totals.cost / totals.clicks).toFixed(2) : '0.00';
  const conv_rate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100).toFixed(2) : '0.00';
  const cost_per_conv = totals.conversions > 0 ? (totals.cost / totals.conversions).toFixed(2) : '0.00';
  return {
    has_data: true,
    has_daily_data: false,
    start_date: perf.start_date,
    end_date: perf.end_date,
    filter_account_id: perf.account_id || null,
    campaigns: camps.map(c => ({ ...c, cost_per_conv: c.cost_per_conversion })),
    totals: {
      clicks: totals.clicks,
      impressions: totals.impressions,
      cost: parseFloat(totals.cost.toFixed(2)),
      avg_cpc,
      avg_ctr,
      conversions: totals.conversions.toFixed(1),
      conv_rate,
      cost_per_conv,
    },
    fraud_impact: null,
    hourly: [],
    devices: [],
    search_terms: [],
    keywords: [],
    geo: [],
  };
}

// ── Sub-components ──

function InfoTip({ text, side = 'top' }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span tabIndex={0} aria-label={text} style={{
        width: 14, height: 14, borderRadius: '50%', border: '1px solid var(--border-strong)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, color: 'var(--text-muted)', cursor: 'help', outline: 'none',
      }}>?</span>
      {open && (
        <span role="tooltip" style={{
          position: 'absolute', zIndex: 50,
          ...(side === 'top'
            ? { bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }
            : { top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }),
          padding: '7px 10px', borderRadius: 6, minWidth: 180, maxWidth: 260,
          background: '#1f2937', color: '#f9fafb', fontSize: 11, fontWeight: 400, lineHeight: 1.45,
          boxShadow: '0 6px 20px -6px rgba(0,0,0,0.35)', whiteSpace: 'normal', textAlign: 'left',
          pointerEvents: 'none',
        }}>
          {text}
          <span style={{
            position: 'absolute',
            ...(side === 'top'
              ? { top: '100%', left: '50%', transform: 'translateX(-50%)', borderTop: '5px solid #1f2937', borderLeft: '5px solid transparent', borderRight: '5px solid transparent' }
              : { bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderBottom: '5px solid #1f2937', borderLeft: '5px solid transparent', borderRight: '5px solid transparent' }),
            width: 0, height: 0,
          }} />
        </span>
      )}
    </span>
  );
}

function StatCard({ label, value, color, icon, trend }) {
  return (
    <div style={{
      flex: '1 1 140px', background: 'var(--bg-card)',
      borderRadius: 10, padding: '12px 14px', position: 'relative', overflow: 'hidden',
      boxShadow: 'var(--shadow-card)',
      transition: 'box-shadow 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-raised)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
    >
      <div style={{
        position: 'absolute', top: 10, right: 12, width: 24, height: 24, borderRadius: 7,
        background: 'var(--bg-hover)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 12,
      }}>{icon}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, letterSpacing: 0, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: color || 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
      {trend !== undefined && (
        <div style={{ fontSize: 11, marginTop: 8, color: trend >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs prev period
        </div>
      )}
    </div>
  );
}

function ThreatBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12, fontWeight: 600, color }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-hover)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function DeviceRow({ device, total, fraud, grandTotal }) {
  const icons = { mobile: '📱', desktop: '🖥️', tablet: '💻' };
  const genuinePct = grandTotal > 0 ? Math.round(((total - fraud) / grandTotal) * 100) : 0;
  const fraudPct = grandTotal > 0 ? Math.round((fraud / grandTotal) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 14 }}>{icons[device] || '🖥️'}</span>
        <span style={{ fontSize: 12, fontWeight: 500, textTransform: 'capitalize', color: 'var(--text-primary)' }}>{device}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{genuinePct + fraudPct}%</span>
      </div>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-hover)' }}>
        {genuinePct > 0 && (
          <div style={{ width: `${genuinePct}%`, background: 'var(--accent-green)', transition: 'width 0.5s' }} />
        )}
        {fraudPct > 0 && (
          <div style={{ width: `${fraudPct}%`, background: 'var(--accent-red)', transition: 'width 0.5s' }} />
        )}
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
      padding: '12px 16px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    }}>
      <div style={{ color: 'var(--text-muted)', marginBottom: 8, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 11, fontWeight: 600 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
            <span style={{ color: 'var(--text-secondary)' }}>{p.name}</span>
          </span>
          <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: p.color }}>
            {p.dataKey === 'fraud_pct' ? `${p.value}%` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

function MapWithTooltip({ topCountries }) {
  const [tooltip, setTooltip] = useState(null);
  const mapRef = useRef(null);
  return (
    <div ref={mapRef} style={{ width: '100%', background: 'var(--bg-secondary)', borderRadius: 10, padding: '12px 8px', marginBottom: 12, position: 'relative' }}>
      {tooltip && (
        <div style={{
          position: 'absolute', left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -120%)',
          background: '#1a1a2e', color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 12,
          pointerEvents: 'none', zIndex: 10, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontWeight: 700, marginBottom: tooltip.fraud != null ? 4 : 0 }}>{tooltip.name}</div>
          {tooltip.fraud != null && (
            <div style={{ fontSize: 11 }}>
              <span style={{ color: '#f87171' }}>{tooltip.fraud} fraud</span>
              <span style={{ color: '#9ca3af' }}> / {tooltip.total} total</span>
            </div>
          )}
          <div style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1a1a2e' }} />
        </div>
      )}
      <ComposableMap projection="geoMercator" projectionConfig={{ scale: 120, center: [10, 30] }} width={780} height={400} style={{ width: '100%', height: 'auto' }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) => geographies.map(geo => {
            const name = geo.properties.name;
            const iso = geo.properties.ISO_A2 || geo.properties.iso_a2 || '';
            const numCode = geo.id;
            const match = topCountries.find(c => c.country === name || (c.code && c.code.length === 2 && c.code === iso) || (c.code && c.code.length === 3 && c.code === numCode));
            const maxFraud = topCountries[0]?.fraud || 1;
            const intensity = match ? Math.max(0.2, match.fraud / maxFraud) : 0;
            return (
              <Geography key={geo.rpiKey || geo.id || name} geography={geo}
                fill={intensity > 0 ? `rgba(185, 28, 28, ${intensity})` : '#d1d5db'} stroke="#fff" strokeWidth={0.4}
                onMouseMove={(e) => { if (!mapRef.current) return; const rect = mapRef.current.getBoundingClientRect(); setTooltip({ name, fraud: match?.fraud, total: match?.total, x: e.clientX - rect.left, y: e.clientY - rect.top }); }}
                onMouseLeave={() => setTooltip(null)}
                style={{ default: { outline: 'none' }, hover: { outline: 'none', fill: intensity > 0 ? '#b91c1c' : '#bbb', cursor: 'pointer' }, pressed: { outline: 'none' } }}
              />
            );
          })}
        </Geographies>
      </ComposableMap>
    </div>
  );
}

// ── Date Picker Component ──

function DateRangePicker({ dateRange, setDateRange, days, setDays, customStart, setCustomStart, customEnd, setCustomEnd, setAppliedStart, setAppliedEnd, loading }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const presets = [
    { key: 'today', label: 'Today', icon: '📅' },
    { key: 'yesterday', label: 'Yesterday', icon: '⏪' },
    { key: '7', label: 'Last 7 days', icon: '📊' },
    { key: '30', label: 'Last 30 days', icon: '📈' },
    { key: '90', label: 'Last 90 days', icon: '📉' },
    { key: '365', label: 'Last year', icon: '🗓️' },
    { key: 'custom', label: 'Custom range', icon: '🔧' },
  ];

  const currentLabel = presets.find(p => p.key === dateRange)?.label || 'Last 30 days';

  const handleSelect = (key) => {
    setDateRange(key);
    if (key !== 'custom') {
      setShowDropdown(false);
      if (key === 'today') {
        const today = new Date().toISOString().slice(0, 10);
        setCustomStart(today); setCustomEnd(today);
        setAppliedStart(today); setAppliedEnd(today);
        setDays(1);
      } else if (key === 'yesterday') {
        const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        setCustomStart(y); setCustomEnd(y);
        setAppliedStart(y); setAppliedEnd(y);
        setDays(1);
      } else {
        setCustomStart(''); setCustomEnd('');
        setAppliedStart(''); setAppliedEnd('');
        setDays(Number(key));
      }
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setShowDropdown(!showDropdown)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
        color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        transition: 'border-color 0.2s', minWidth: 180,
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-blue)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        <span style={{ fontSize: 15 }}>📅</span>
        <span style={{ flex: 1, textAlign: 'left' }}>
          {currentLabel}
          {loading && <span style={{ marginLeft: 8, fontWeight: 500, color: 'var(--text-muted)', fontSize: 12 }}>Loading…</span>}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', transform: showDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
      </button>

      {showDropdown && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 280,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 12px 36px rgba(0,0,0,0.2)', zIndex: 100, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 1 }}>
            Select period
          </div>
          {presets.map(p => (
            <button key={p.key} onClick={() => handleSelect(p.key)} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px',
              border: 'none', background: dateRange === p.key ? 'rgba(88,166,255,0.1)' : 'transparent',
              color: dateRange === p.key ? 'var(--accent-blue)' : 'var(--text-primary)',
              fontSize: 13, fontWeight: dateRange === p.key ? 600 : 400, cursor: 'pointer',
              borderLeft: dateRange === p.key ? '3px solid var(--accent-blue)' : '3px solid transparent',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (dateRange !== p.key) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
            onMouseLeave={e => { if (dateRange !== p.key) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 15 }}>{p.icon}</span>
              {p.label}
            </button>
          ))}

          {dateRange === 'custom' && (
            <div style={{ padding: '12px 14px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'none' }}>Custom range</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', fontSize: 12, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', fontSize: 12, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
              </div>
              <button onClick={() => {
                if (customStart && customEnd) {
                  setAppliedStart(customStart);
                  setAppliedEnd(customEnd);
                }
                setShowDropdown(false);
              }} disabled={!customStart || !customEnd} style={{
                padding: '8px', background: customStart && customEnd ? 'var(--accent-blue)' : 'var(--border)',
                color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: customStart && customEnd ? 'pointer' : 'default', marginTop: 4,
              }}>Apply</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──

export default function Dashboard({ campaignId, campaigns = [], user }) {
  const [usage, setUsage] = useState(null);
  const [stats, setStats] = useState(null);
  const [threats, setThreats] = useState([]);
  const [campaignFraud, setCampaignFraud] = useState([]);
  const [gadsData, setGadsData] = useState(null);
  const [gadsError, setGadsError] = useState(null);
  const [days, setDays] = useState(30);
  const [dateRange, setDateRange] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [chartMode, setChartMode] = useState('hourly');
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');

  // Google Ads fetch window (kept in sync with main date-range picker below)
  const [gadsFetchStart, setGadsFetchStart] = useState('');
  const [gadsFetchEnd, setGadsFetchEnd] = useState('');

  const [fraudStart, setFraudStart] = useState('');
  const [fraudEnd, setFraudEnd] = useState('');
  const [cpcInput, setCpcInput] = useState('');
  const [cpcSaving, setCpcSaving] = useState(false);
  const [fraudLoading, setFraudLoading] = useState(false);
  const [gadsLoading, setGadsLoading] = useState(false);

  // Init fraud date range to last 30 days (Google Ads convention: ends yesterday).
  useEffect(() => {
    const today = new Date(), fmt = d => d.toISOString().slice(0, 10);
    const e = new Date(today); e.setDate(e.getDate() - 1);
    const s = new Date(today); s.setDate(s.getDate() - 30);
    setFraudStart(fmt(s)); setFraudEnd(fmt(e));
  }, []);

  // Seed CPC from saved campaign value (fallback)
  useEffect(() => {
    if (stats?.cpc != null) setCpcInput(String(stats.cpc));
  }, [campaignId, stats?.cpc]);

  // Override CPC with live Google Ads avg_cpc when available — auto-save to campaign
  useEffect(() => {
    if (!gadsData) return;
    const gaAvgCpc = parseFloat(gadsData.totals?.avg_cpc);
    if (!gaAvgCpc || gaAvgCpc <= 0) return;
    const rounded = gaAvgCpc.toFixed(2);
    setCpcInput(rounded);
    if (campaignId) {
      campaignsAPI.update(campaignId, { cpc: gaAvgCpc }).catch(() => {});
    }
  }, [gadsData, campaignId]);

  const handleCpcBlur = async () => {
    const val = parseFloat(cpcInput);
    if (isNaN(val) || val < 0 || !campaignId) return;
    if (val === (stats?.cpc || 0)) return;
    setCpcSaving(true);
    try {
      await campaignsAPI.update(campaignId, { cpc: val });
    } catch (_) {}
    setCpcSaving(false);
  };

  useEffect(() => {
    billingAPI.getUsage().then(setUsage).catch(() => {});
  }, []);

  // Campaign fraud is derived from stats — no separate API call needed

  useEffect(() => {
    if (!campaignId || !gadsFetchStart || !gadsFetchEnd) return;
    let cancelled = false;
    setGadsLoading(true);
    setGadsError(null);
    googleAdsAPI.getPerformance(campaignId, gadsFetchStart, gadsFetchEnd)
      .then(d => { if (!cancelled) { setGadsData(transformGadsPerf(d)); setGadsError(null); setGadsLoading(false); } })
      .catch(e => {
        if (!cancelled) {
          setGadsData(null);
          setGadsError(e?.response?.data?.detail || e?.message || 'Failed to load Google Ads data');
          setGadsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [campaignId, gadsFetchStart, gadsFetchEnd]);

  // Load default range on mount / campaign change
  useEffect(() => {
    if (!campaignId) { setCampaignFraud([]); setGadsData(null); setGadsError(null); return; }
  }, [campaignId]);

  // Track applied custom dates separately so we only fetch when both are set
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');

  // Keep Google Ads fetch window in sync with the main date-range picker
  useEffect(() => {
    if (!campaignId) return;
    if (appliedStart && appliedEnd) {
      setGadsFetchStart(appliedStart);
      setGadsFetchEnd(appliedEnd);
      return;
    }
    // Match Google Ads convention: "last N days" ends yesterday, not today.
    const today = new Date();
    const fmt = d => d.toISOString().slice(0, 10);
    const end = new Date(today); end.setDate(end.getDate() - 1);
    const start = new Date(today); start.setDate(start.getDate() - (days || 30));
    setGadsFetchStart(fmt(start));
    setGadsFetchEnd(fmt(end));
  }, [campaignId, days, appliedStart, appliedEnd]);
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  useEffect(() => {
    if (!campaignId) { setStats(null); setThreats([]); setError(''); return; }
    if (dateRangeRef.current === 'custom' && (!appliedStart || !appliedEnd)) return;
    let cancelled = false;
    let retryTimer = null;

    const cacheKey = `dashboard_${campaignId}_${days}_${appliedStart}_${appliedEnd}`;

    // Show cached data immediately so the page renders instantly
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { stats: cs, threats: ct, fraud: cf, ts } = JSON.parse(cached);
        if (cs) setStats(cs);
        if (ct) setThreats(ct);
        if (cf) { setCampaignFraud(cf); setFraudLoading(false); }
        const age = Math.round((Date.now() - ts) / 1000);
        setLastUpdated(new Date(ts));
        setError('');
        // If cache is fresh enough (< 2 min), skip the loading spinner
        if (age < 120) { setLoading(false); } else { setLoading(true); }
      } else {
        setStats(null); setThreats([]);
        setLoading(true);
      }
    } catch (_) {
      setStats(null); setThreats([]);
      setLoading(true);
    }

    const fetchData = async (attempt = 1) => {
      if (cancelled) return;
      try {
        const combined = await dashboardAPI.getCombined(campaignId, days, appliedStart || null, appliedEnd || null);
        if (!cancelled) {
          if (combined.stats) setStats(combined.stats);
          if (combined.threats) setThreats(combined.threats);
          if (combined.fraud) { setCampaignFraud(combined.fraud); setFraudLoading(false); }
          const now = Date.now();
          setLastUpdated(new Date(now));
          setLoading(false);
          setError('');
          // Save to cache
          try { localStorage.setItem(cacheKey, JSON.stringify({ stats: combined.stats, threats: combined.threats, fraud: combined.fraud, ts: now })); } catch (_) {}
        }
      } catch (e) {
        if (cancelled) return;
        const isTimeout = e?.code === 'ECONNABORTED' || e?.message?.includes('timeout');
        if (isTimeout && attempt < 4) {
          const wait = attempt * 8;
          setRetrying(true);
          setError(`Server is starting up — retrying in ${wait}s… (${attempt}/3)`);
          retryTimer = setTimeout(() => fetchData(attempt + 1), wait * 1000);
        } else {
          setRetrying(false);
          setLoading(false);
          if (!stats) setError(isTimeout ? 'Server took too long to respond. Please refresh the page.' : 'Failed to load dashboard data.');
        }
      }
    };

    fetchData();
    const timer = setInterval(() => fetchData(), REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); clearTimeout(retryTimer); };
  }, [campaignId, days, appliedStart, appliedEnd]);

  if (!campaignId) {
    return (
      <div>
        <div className="page-header"><h1>Overview</h1></div>
        <div className="empty">
          <h3>No campaign selected</h3>
          <p>Select a campaign from the sidebar dropdown to view live stats.</p>
          <NavLink to="/campaigns" className="btn btn-primary" style={{ marginTop: 12, display: 'inline-block' }}>Go to Campaigns →</NavLink>
        </div>
      </div>
    );
  }

  // Show skeleton while loading or retrying (never blank page)
  if (!stats && (loading || retrying || !error)) return <DashboardSkeleton />;
  if (!stats && error) return (
    <div>
      <div className="page-header"><h1>Overview</h1></div>
      <div style={{ padding: 20, background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 12, color: 'var(--accent-red)', fontSize: 14, fontWeight: 500 }}>
        {error}
      </div>
    </div>
  );

  const tl = stats.threat_levels || {};
  const tlTotal = stats.total_clicks || 1;
  const hourlyWithClicks = (stats.hourly_breakdown || []).filter(h => h.total > 0);
  const spikes = [...hourlyWithClicks].sort((a, b) => b.fraud_pct - a.fraud_pct).slice(0, 2);

  const chartData = chartMode === 'hourly'
    ? (stats.hourly_breakdown || [])
    : (stats.daily_breakdown || []).map(d => ({
        ...d, label: d.date,
        fraud_pct: d.total > 0 ? Math.round(d.fraud / d.total * 100) : 0,
        genuine: (d.total || 0) - (d.fraud || 0),
      }));

  const fraudTypePie = Object.entries(stats.fraud_types || {}).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const topCountries = stats.top_countries || [];
  const deviceEntries = Object.entries(stats.device_breakdown || {});
  const fraudRate = stats.total_clicks > 0 ? Math.round((stats.fraud_clicks || 0) / stats.total_clicks * 100) : 0;

  return (
    <div className="dashboard-content-enter">
      {/* Inline retry banner — only shown when we already have stats to display */}
      {error && stats && (
        <div style={{ marginBottom: 12, padding: '10px 16px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: 8, color: '#f0b429', fontSize: 13 }}>
          {error}
        </div>
      )}
      {/* Greeting header */}
      {(() => {
        const activeCamp = campaigns.find(c => c.id === campaignId);
        const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0] || 'there';
        const nameCap = firstName.charAt(0).toUpperCase() + firstName.slice(1);
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--text-primary)', margin: 0 }}>Hello, {nameCap}!</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span>Here's how much click fraud we've blocked{activeCamp ? <> for <a href={`https://${activeCamp.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>{activeCamp.domain} ↗</a></> : ''}.</span>
                {loading && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent-blue)' }}><div className="spinner" style={{ width: 10, height: 10, borderWidth: 2 }} /> Refreshing</span>}
              </div>
            </div>
            <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} days={days} setDays={setDays}
              customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd}
              setAppliedStart={setAppliedStart} setAppliedEnd={setAppliedEnd}
              loading={loading || gadsLoading} />
          </div>
        );
      })()}

      {/* Hero: ad spend saved — the one number worth screenshotting */}
      {stats && (() => {
        const cpcVal = parseFloat(cpcInput) || 0;
        const fraudClicks = stats.fraud_clicks || stats.clicks_saved || 0;
        const savings = stats.estimated_savings || (fraudClicks * cpcVal);
        if (savings <= 0) return null;
        const totalClicks = stats.total_clicks || 0;
        const fraudPct = totalClicks > 0 ? ((fraudClicks / totalClicks) * 100).toFixed(1) : '0.0';
        const periodLabel = (dateRange === 'custom' && customStart && customEnd)
          ? `${customStart} → ${customEnd}`
          : `last ${days} days`;
        const annualProjection = (savings / Math.max(days, 1)) * 365;
        const dailyAvg = savings / Math.max(days, 1);
        const fmtMoney = (n, dp = 0) => n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

        return (
          <div style={{
            position: 'relative', marginBottom: 20, padding: '22px 28px', borderRadius: 14,
            background: 'linear-gradient(135deg, #0b3d2a 0%, #10553a 55%, #1a7a52 100%)',
            boxShadow: '0 10px 32px -14px rgba(16,85,58,0.55), 0 1px 0 rgba(255,255,255,0.08) inset',
            overflow: 'hidden',
          }}>
            {/* Decorative concentric rings — subtle, keeps it premium */}
            <svg aria-hidden="true" width="340" height="340" viewBox="0 0 340 340"
              style={{ position: 'absolute', right: -90, top: -90, opacity: 0.08, pointerEvents: 'none' }}>
              <defs>
                <radialGradient id="heroRing" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0"/>
                </radialGradient>
              </defs>
              {[60, 100, 140, 170].map(r => (
                <circle key={r} cx="170" cy="170" r={r} fill="none" stroke="url(#heroRing)" strokeWidth="1" />
              ))}
            </svg>

            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 28, flexWrap: 'wrap' }}>

              {/* Left: shield + amount */}
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" fill="#ffffff" fillOpacity="0.9"/>
                    <path d="M8.5 12l2.5 2.5L15.5 10" stroke="#0b3d2a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 6 }}>
                    Ad spend saved · {periodLabel}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, color: '#ffffff', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.025em', lineHeight: 1 }}>
                    <span style={{ fontSize: 24, fontWeight: 600, color: 'rgba(255,255,255,0.75)', transform: 'translateY(-6px)' }}>$</span>
                    <span style={{ fontSize: 42, fontWeight: 700 }}>{fmtMoney(savings, 2)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
                      {fraudClicks.toLocaleString()} fraud clicks blocked
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                      background: 'rgba(255,255,255,0.14)', color: '#ffffff', letterSpacing: 0.3,
                    }}>
                      {fraudPct}% of traffic
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: supporting stats grid */}
              {cpcVal > 0 && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: 1,
                  background: 'rgba(255,255,255,0.1)', borderRadius: 10, overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.14)',
                  flexShrink: 0,
                }}>
                  {[
                    { label: 'CPC', value: `$${cpcVal.toFixed(2)}` },
                    { label: 'Daily avg', value: `$${fmtMoney(dailyAvg, 2)}` },
                    { label: 'Projected / year', value: `$${fmtMoney(annualProjection, 0)}`, bold: true },
                  ].map((s, i) => (
                    <div key={s.label} style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
                      padding: '12px 16px', minWidth: 110, textAlign: 'left',
                    }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>
                        {s.label}
                      </div>
                      <div style={{
                        fontSize: s.bold ? 16 : 14, fontWeight: s.bold ? 700 : 600,
                        color: '#ffffff', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums',
                      }}>
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Click usage bar */}
      {usage && !usage.unlimited && (
        <div style={{
          background: 'var(--bg-card)', border: `1px solid ${usage.percent >= 100 ? 'var(--accent-red)' : usage.percent >= 80 ? 'rgba(240,180,41,0.4)' : 'var(--border)'}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Monthly Click Usage
            </span>
            <span style={{ fontSize: 12, color: usage.percent >= 100 ? 'var(--accent-red)' : usage.percent >= 80 ? '#f0b429' : 'var(--text-muted)', fontWeight: 600 }}>
              {usage.clicks_this_month.toLocaleString()} / {usage.limit.toLocaleString()} clicks
              {usage.percent >= 100 && ' — Limit reached'}
              {usage.percent >= 80 && usage.percent < 100 && ' — Approaching limit'}
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4, transition: 'width 0.4s',
              width: `${usage.percent}%`,
              background: usage.percent >= 100 ? 'var(--accent-red)' : usage.percent >= 80 ? '#f0b429' : 'var(--accent-green)',
            }} />
          </div>
          {usage.percent >= 80 && (
            <div style={{ fontSize: 11, color: usage.percent >= 100 ? 'var(--accent-red)' : '#f0b429', marginTop: 6 }}>
              {usage.percent >= 100
                ? 'You\'ve reached your monthly click limit. Upgrade your plan to continue tracking.'
                : `You've used ${usage.percent}% of your monthly clicks. Consider upgrading soon.`}
              {' '}<a href="/settings?tab=billing" style={{ color: 'var(--accent-blue)', fontWeight: 600, textDecoration: 'none' }}>Upgrade →</a>
            </div>
          )}
        </div>
      )}

      {/* ClickGuard-style 4 metric tiles with trend chips */}
      {(() => {
        const cpcVal = parseFloat(cpcInput) || 0;
        const totalClicks = stats.total_clicks || 0;
        const fraudClicks = stats.fraud_clicks || stats.clicks_saved || 0;
        const legitClicks = stats.legitimate_clicks || 0;
        const savings = stats.estimated_savings || (fraudClicks * cpcVal);
        const adSpent = (gadsData?.totals?.cost) ? Number(gadsData.totals.cost) : (totalClicks * cpcVal);
        const blocked = stats.blocked_ips || 0;
        const roi = savings > 0 && adSpent > 0 ? ((savings / adSpent) * 100) : 0;

        const tiles = [
          { label: 'Processed ad spend', value: `$${adSpent.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, help: 'Total spend on ads processed in this period' },
          { label: 'Prevented ad waste', value: `$${savings.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, help: 'Estimated spend saved by blocking fraud', accent: 'var(--accent-green)' },
          { label: 'Blocked clicks', value: fraudClicks.toLocaleString(), help: 'Clicks blocked as fraudulent', accent: 'var(--accent-red)' },
          { label: 'Clean traffic', value: `${stats.cleaner_traffic_percent || 0}%`, help: 'Percentage of traffic that was legitimate', accent: 'var(--accent-blue)' },
        ];

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
            {tiles.map(t => (
              <div key={t.label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{t.label}</span>
                  <InfoTip text={t.help} />
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.015em', color: t.accent || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{t.value}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>vs. last period</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Traffic Quality + Campaign Performance row */}
      {(() => {
        const total = stats.total_clicks || 0;
        const fraud = stats.fraud_clicks || 0;
        const legit = stats.legitimate_clicks || 0;
        const suspicious = Math.max(0, total - legit - fraud);
        const legitPct = total > 0 ? (legit / total) * 100 : 0;
        const suspiciousPct = total > 0 ? (suspicious / total) * 100 : 0;
        const fraudPct = total > 0 ? (fraud / total) * 100 : 0;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Traffic quality</span>
                <InfoTip text="Split of your paid traffic into legitimate visitors (safe for conversion optimisation), suspicious (flagged but not blocked), and fraud (auto-excluded from Google Ads)." />
              </div>
              <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'var(--bg-hover)', marginBottom: 14 }}>
                {legitPct > 0 && <div style={{ width: `${legitPct}%`, background: 'var(--accent-green)' }} />}
                {suspiciousPct > 0 && <div style={{ width: `${suspiciousPct}%`, background: 'var(--accent-orange)' }} />}
                {fraudPct > 0 && <div style={{ width: `${fraudPct}%`, background: 'var(--accent-red)' }} />}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                  { label: 'Processed', value: total.toLocaleString(), color: 'var(--text-primary)', dot: 'var(--text-secondary)', sub: `from total ${total.toLocaleString()}` },
                  { label: 'Valid', value: legit.toLocaleString(), color: 'var(--accent-green)', dot: 'var(--accent-green)' },
                  { label: 'Suspicious', value: suspicious.toLocaleString(), color: 'var(--accent-orange)', dot: 'var(--accent-orange)' },
                  { label: 'Invalid', value: fraud.toLocaleString(), color: 'var(--accent-red)', dot: 'var(--accent-red)' },
                ].map(m => (
                  <div key={m.label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      {m.label}
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.dot }} />
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: m.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{m.value}</div>
                    {m.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{m.sub}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Campaign performance — pulled from Google Ads if available, else top countries */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 16px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Campaign performance</span>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', maxHeight: 220, overflowY: 'auto' }}>
                {gadsData && gadsData.has_data && gadsData.campaigns && gadsData.campaigns.length > 0 ? (
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th style={{ textAlign: 'right' }}>Valid</th>
                        <th style={{ textAlign: 'right' }}>Invalid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gadsData.campaigns.slice(0, 8).map((c, i) => {
                        const clicks = Number(c.clicks) || 0;
                        const rate = total > 0 ? fraud / total : 0;
                        const invalid = Math.round(clicks * rate);
                        const valid = clicks - invalid;
                        return (
                          <tr key={i}>
                            <td style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{c.name}</td>
                            <td style={{ textAlign: 'right', color: 'var(--accent-green)', fontVariantNumeric: 'tabular-nums' }}>{valid.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', color: 'var(--accent-red)', fontVariantNumeric: 'tabular-nums' }}>{invalid.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No campaign data yet</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Savings Calculator (flat Shopify-style) ── */}
      {(() => {
        const cpcVal = parseFloat(cpcInput) || 0;
        const clicksSaved = stats.clicks_saved || 0;
        const liveSavings = (clicksSaved * cpcVal).toFixed(2);
        const legitClicks = stats.legitimate_clicks || 0;
        const blockedIPs = stats.blocked_ips || 0;
        const cleanPct = stats.cleaner_traffic_percent || 0;
        const totalClicks = stats.total_clicks || 0;
        const budgetProtected = (legitClicks * cpcVal).toFixed(2);
        const fraudRate = totalClicks > 0 ? ((clicksSaved / totalClicks) * 100).toFixed(1) : '0.0';
        const qualityScore = cleanPct >= 90 ? 'Excellent' : cleanPct >= 75 ? 'Good' : cleanPct >= 60 ? 'Fair' : 'Poor';
        const projectedMonthly = days <= 7 ? (parseFloat(liveSavings) * (30 / (days || 7))).toFixed(2) : null;
        const autoFilled = gadsData && parseFloat(gadsData.totals?.avg_cpc) > 0;

        const metrics = [
          { value: clicksSaved.toLocaleString(), label: 'Fraud blocked', sub: `${fraudRate}% of traffic`, color: 'var(--accent-red)' },
          { value: `$${parseFloat(liveSavings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, label: 'Ad spend saved', sub: projectedMonthly ? `~$${parseFloat(projectedMonthly).toFixed(2)}/mo` : `${days}d period`, color: 'var(--accent-green)' },
          { value: `$${parseFloat(budgetProtected).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, label: 'Genuine traffic value', sub: `${legitClicks.toLocaleString()} real visitors`, color: 'var(--accent-blue)' },
          { value: blockedIPs.toLocaleString(), label: 'IPs banned', sub: `${qualityScore} · ${cleanPct}% clean`, color: 'var(--text-primary)' },
        ];

        return (
          <div className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
            {/* Header + formula */}
            <div style={{ padding: '12px 16px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Savings calculator</div>
                {autoFilled && (
                  <span style={{ fontSize: 11, color: 'var(--accent-green)', background: '#e3f2ed', borderRadius: 999, padding: '2px 10px', fontWeight: 500 }}>CPC from Google Ads</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>Fraud clicks</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--accent-red)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', lineHeight: 1.1 }}>{clicksSaved.toLocaleString()}</div>
                </div>
                <div style={{ fontSize: 15, color: 'var(--text-muted)', padding: '0 2px 4px' }}>×</div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Cost per click {cpcSaving && <span style={{ color: 'var(--accent-blue)', fontSize: 10 }}>Saving…</span>}
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border-strong)', borderRadius: 7, overflow: 'hidden', background: 'var(--bg-secondary)' }}>
                    <span style={{ padding: '0 8px', fontSize: 13, color: 'var(--text-secondary)', borderRight: '1px solid var(--border)', lineHeight: '30px', userSelect: 'none' }}>$</span>
                    <input type="text" inputMode="decimal" value={cpcInput}
                      onChange={e => setCpcInput(e.target.value.replace(/[^0-9.]/g, ''))}
                      onBlur={handleCpcBlur} placeholder="0.00"
                      style={{ width: 70, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', padding: '5px 10px', textAlign: 'right' }}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 15, color: 'var(--text-muted)', padding: '0 2px 4px' }}>=</div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>Saved this period</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--accent-green)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                    ${parseFloat(liveSavings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics row */}
            <div style={{ borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {metrics.map((m, i) => (
                <div key={i} style={{ padding: '10px 16px', borderRight: i < 3 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontSize: 17, fontWeight: 600, color: m.color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', lineHeight: 1.1 }}>{m.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, marginTop: 2 }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{m.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Chart — full width */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 14 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Visits over time</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {spikes.length > 0 && (
              <div style={{ display: 'flex', gap: 6 }}>
                {spikes.map((s, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
                    background: i === 0 ? '#fee9e5' : '#e3f2ed',
                    borderRadius: 999, fontSize: 12, fontWeight: 500,
                    color: i === 0 ? '#8e1a00' : '#005c42',
                  }}>
                    <span>{i === 0 ? '↑' : '↓'}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.label} · {s.fraud_pct}%</span>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 2, background: 'var(--bg-hover)', borderRadius: 8, padding: 2 }}>
              {['daily', 'hourly'].map(mode => (
                <button key={mode} onClick={() => setChartMode(mode)} style={{
                  padding: '5px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: chartMode === mode ? 'var(--bg-card)' : 'transparent',
                  color: chartMode === mode ? 'var(--text-primary)' : 'var(--text-secondary)',
                  boxShadow: chartMode === mode ? 'var(--shadow-card)' : 'none',
                  transition: 'all 0.12s',
                }}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 40, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="genuineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#008060" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#008060" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="fraudGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#d82c0d" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#d82c0d" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--border)" opacity={0.6} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickLine={false} axisLine={false} interval={chartMode === 'hourly' ? 1 : 'preserveStartEnd'} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickLine={false} axisLine={false} domain={[0, 100]} />
            <Tooltip content={<ChartTooltip />} />
            <Area yAxisId="left" type="monotone" dataKey="genuine" name="Genuine" stackId="a"
              stroke="#008060" strokeWidth={2} fill="url(#genuineGrad)" dot={false} activeDot={{ r: 4, fill: '#008060', strokeWidth: 0 }} />
            <Area yAxisId="left" type="monotone" dataKey="fraud" name="Fraud" stackId="a"
              stroke="#d82c0d" strokeWidth={2} fill="url(#fraudGrad)" dot={false} activeDot={{ r: 4, fill: '#d82c0d', strokeWidth: 0 }} />
            <Line yAxisId="right" type="monotone" dataKey="fraud_pct" name="Fraud %" stroke="#b98900" strokeWidth={2}
              dot={false} activeDot={{ r: 4, fill: '#b98900', strokeWidth: 0 }} strokeDasharray="4 3" />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
          {[['#008060', 'Genuine', false], ['#d82c0d', 'Fraud', false], ['#b98900', 'Fraud %', true]].map(([color, label, isLine]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {isLine
                ? <div style={{ width: 18, height: 2, background: color, borderRadius: 1 }} />
                : <div style={{ width: 10, height: 10, borderRadius: 3, background: color, opacity: 0.85 }} />
              }
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Period summary strip */}
        {chartData.length > 0 && (() => {
          const periodTotal = chartData.reduce((s, d) => s + (d.genuine || 0) + (d.fraud || 0), 0);
          const periodFraud = chartData.reduce((s, d) => s + (d.fraud || 0), 0);
          const periodGenuine = periodTotal - periodFraud;
          const peakDay = [...chartData].filter(d => (d.genuine || 0) + (d.fraud || 0) > 0).sort((a, b) => b.fraud_pct - a.fraud_pct)[0];
          const items = [
            { label: 'Total Visits', value: periodTotal.toLocaleString(), color: 'var(--accent-blue)', bg: 'rgba(88,166,255,0.08)', border: 'rgba(88,166,255,0.2)' },
            { label: 'Genuine Clicks', value: periodGenuine.toLocaleString(), color: '#3fb950', bg: 'rgba(63,185,80,0.08)', border: 'rgba(63,185,80,0.2)' },
            { label: 'Fraud Clicks', value: periodFraud.toLocaleString(), color: '#f85149', bg: 'rgba(248,81,73,0.08)', border: 'rgba(248,81,73,0.2)' },
            { label: 'Peak Fraud', value: peakDay ? `${peakDay.fraud_pct}%` : '—', sub: peakDay?.label, color: '#e3b341', bg: 'rgba(227,179,65,0.08)', border: 'rgba(227,179,65,0.2)' },
          ];
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
              {items.map(item => (
                <div key={item.label} style={{
                  background: item.bg, borderRadius: 8, padding: '6px 10px',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ width: 2, alignSelf: 'stretch', borderRadius: 1, background: item.color, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: item.color, lineHeight: 1.1 }}>
                      {item.value}
                      {item.sub && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 4 }}>{item.sub}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Insight cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>

        {/* Fraud Rate */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>Fraud rate</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums', color: fraudRate > 20 ? 'var(--accent-red)' : fraudRate > 5 ? 'var(--accent-orange)' : 'var(--accent-green)', lineHeight: 1 }}>
              {fraudRate}%
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', paddingBottom: 2, lineHeight: 1.3 }}>
              {stats.fraud_clicks || 0} fraud<br />{stats.total_clicks || 0} total
            </div>
          </div>
          {stats.total_clicks > 0 && (() => {
            const genuinePct = 100 - fraudRate;
            return (
              <>
                <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--bg-secondary)', display: 'flex', marginBottom: 6 }}>
                  <div style={{ width: `${genuinePct}%`, background: 'linear-gradient(90deg, #3fb950, #22c55e)', transition: 'width 0.6s ease' }} />
                  <div style={{ width: `${fraudRate}%`, background: 'linear-gradient(90deg, #f85149, #dc2626)', transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 10, color: '#3fb950', fontWeight: 600 }}>{genuinePct}% clean</span>
                  <span style={{ fontSize: 10, color: '#f85149', fontWeight: 600 }}>{fraudRate}% fraud</span>
                </div>
              </>
            );
          })()}
          {chartData.length > 1 && (() => {
            const withData = chartData.filter(d => (d.genuine || 0) + (d.fraud || 0) > 0);
            if (withData.length < 2) return null;
            const peak = [...withData].sort((a, b) => b.fraud_pct - a.fraud_pct)[0];
            const clean = [...withData].sort((a, b) => a.fraud_pct - b.fraud_pct)[0];
            return (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', background: 'rgba(248,81,73,0.07)', border: '1px solid rgba(248,81,73,0.15)', borderRadius: 8 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Peak fraud</span>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: '#f85149' }}>{peak.label} · {peak.fraud_pct}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', background: 'rgba(63,185,80,0.07)', border: '1px solid rgba(63,185,80,0.15)', borderRadius: 8 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Cleanest</span>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: '#3fb950' }}>{clean.label} · {clean.fraud_pct}%</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Threat Level */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>Threat level</div>
          <ThreatBar label="Low" value={tl.low || 0} total={tlTotal} color="var(--accent-green)" />
          <ThreatBar label="Substantial" value={tl.substantial || 0} total={tlTotal} color="var(--accent-orange)" />
          <ThreatBar label="Critical" value={tl.critical || 0} total={tlTotal} color="var(--accent-red)" />
        </div>

        {/* Fraud by Device */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>Fraud by device</div>
          {deviceEntries.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No device data yet</div>
          ) : deviceEntries.map(([device, data]) => (
            <DeviceRow key={device} device={device} total={data.total} fraud={data.fraud} grandTotal={stats.total_clicks} />
          ))}
        </div>
      </div>

      {/* ══ GOOGLE ADS PERFORMANCE ══ */}
      {gadsError && (() => {
        const isNotConnected = /not connected|no refresh token|reconnect/i.test(gadsError);
        return isNotConnected ? (
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--bg-secondary)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 48 48"><path d="M33.6 43.2L43.2 26.4 33.6 9.6 24 26.4z" fill="#FBBC04"/><path d="M14.4 43.2L24 26.4 14.4 9.6 4.8 26.4z" fill="#4285F4"/><path d="M24 26.4L33.6 9.6H14.4L24 26.4z" fill="#34A853"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>Google Ads not connected</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Connect your account to see ad spend, campaign performance, and fraud estimates.</div>
                </div>
              </div>
              <a href="/google-ads" style={{ flexShrink: 0, padding: '8px 18px', fontSize: 12, fontWeight: 600, borderRadius: 8, background: 'var(--accent-blue)', color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                Connect Google Ads
              </a>
            </div>
          </div>
        ) : (
          <div className="card" style={{ marginBottom: 24, borderColor: 'rgba(248,81,73,0.35)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="10" cy="10" r="9" stroke="var(--accent-red)" strokeWidth="1.5"/>
                <path d="M10 6v5M10 13.5v.5" stroke="var(--accent-red)" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-red)', marginBottom: 3 }}>Google Ads — Access Error</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{gadsError}</div>
              </div>
            </div>
          </div>
        );
      })()}
      {!gadsError && gadsData && !gadsData.has_data && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="24" height="24" viewBox="0 0 48 48">
              <path d="M33.6 43.2L43.2 26.4 33.6 9.6 24 26.4z" fill="#FBBC04"/>
              <path d="M14.4 43.2L24 26.4 14.4 9.6 4.8 26.4z" fill="#4285F4"/>
              <path d="M24 26.4L33.6 9.6H14.4L24 26.4z" fill="#34A853"/>
            </svg>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Google Ads Performance</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {gadsData.filter_account_id
                  ? `Linked to Account ${gadsData.filter_account_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')} — No active campaigns found`
                  : 'Not linked — Set the Google Ads Customer ID in Google Ads Sync page'}
              </div>
            </div>
          </div>
        </div>
      )}

      {!gadsError && gadsData && gadsData.has_data && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingTop: 8, flexWrap: 'wrap' }}>
            <svg width="24" height="24" viewBox="0 0 48 48">
              <path d="M33.6 43.2L43.2 26.4 33.6 9.6 24 26.4z" fill="#FBBC04"/>
              <path d="M14.4 43.2L24 26.4 14.4 9.6 4.8 26.4z" fill="#4285F4"/>
              <path d="M24 26.4L33.6 9.6H14.4L24 26.4z" fill="#34A853"/>
            </svg>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary)', margin: 0 }}>Google Ads performance</h2>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span>{gadsData.start_date} to {gadsData.end_date}</span>
                {gadsData.filter_account_id && (
                  <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 7px', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 10, color: 'var(--text-secondary)' }}>
                    Account {gadsData.filter_account_id}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {gadsLoading && <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
              <RangePicker value="30" onApply={(s, e) => { setGadsFetchStart(s); setGadsFetchEnd(e); }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
            <StatCard label="Ad Clicks" value={(gadsData.totals.clicks || 0).toLocaleString()} icon="🖱️" color="var(--accent-blue)" />
            <StatCard label="Impressions" value={(gadsData.totals.impressions || 0).toLocaleString()} icon="👁️" color="var(--text-primary)" />
            <StatCard label="Ad Spend" value={`$${(gadsData.totals.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} icon="💳" color="var(--accent-orange)" />
            <StatCard label="Avg CPC" value={`$${gadsData.totals.avg_cpc}`} icon="📊" color="var(--accent-blue)" />
            <StatCard label="CTR" value={`${gadsData.totals.avg_ctr}%`} icon="📈" color="var(--accent-green)" />
            <StatCard label="Conversions" value={gadsData.totals.conversions} icon="🎯" color="var(--accent-green)" />
            <StatCard label="Conv Rate" value={`${gadsData.totals.conv_rate}%`} icon="⚡" color="var(--accent-blue)" />
            <StatCard label="Cost/Conv" value={`$${gadsData.totals.cost_per_conv}`} icon="💰" color="var(--accent-orange)" />
          </div>

          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 24px', marginBottom: 24, overflow: 'auto', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'none', letterSpacing: 0.5 }}>Campaign Breakdown</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                  {gadsFetchStart && gadsFetchEnd
                    ? `${new Date(gadsFetchStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(gadsFetchEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : 'Last 30 days'}
                </div>
              </div>
              {gadsLoading && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--accent-blue)' }}><div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />Updating...</div>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Campaign', 'Status', 'Clicks', 'Impr.', 'CTR', 'Avg CPC', 'Cost', 'Conv.'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Campaign' ? 'left' : 'right', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'none', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gadsData.campaigns.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px', color: 'var(--text-primary)', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: c.status === 'ENABLED' ? 'rgba(63,185,80,0.1)' : 'rgba(156,163,175,0.1)', color: c.status === 'ENABLED' ? 'var(--accent-green)' : 'var(--text-muted)' }}>
                        {c.status === 'ENABLED' ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-blue)', fontWeight: 600 }}>{c.clicks.toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{c.impressions.toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{c.ctr}%</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>${c.avg_cpc}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-orange)', fontWeight: 600 }}>${c.cost.toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-green)', fontWeight: 600 }}>{c.conversions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Fraudulent Campaigns */}
      <div style={{ marginBottom: 14 }}>
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Fraudulent campaigns</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                {fraudStart && fraudEnd
                  ? `${new Date(fraudStart+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(fraudEnd+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`
                  : 'Last 30 days'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {fraudLoading && <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
              <RangePicker value="30" onApply={(s, e) => { setFraudStart(s); setFraudEnd(e); setGadsFetchStart(s); setGadsFetchEnd(e); }} />
            </div>
          </div>

          {/* ── Campaign fraud breakdown from stats ── */}
          {stats && (() => {
            const campaignName = campaigns.find(c => c.id === campaignId)?.name || 'Active Campaign';
            const fraudTypes = stats.fraud_types || {};
            const total = stats.total_clicks || 0;
            const fraud = stats.fraud_clicks || 0;
            if (total === 0) return null;
            return (
              <div style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green)', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{campaignName}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {fraud} fraud / {total} total
                  </span>
                </div>
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
                  {Object.entries(FRAUD_TYPE_COLORS).map(([key, color]) => fraudTypes[key] > 0 && (
                    <div key={key} style={{ flex: fraudTypes[key], background: color, minWidth: 3 }} title={`${key}: ${fraudTypes[key]}`} />
                  ))}
                  {fraud === 0 && <div style={{ flex: 1, background: 'var(--bg-hover)' }} />}
                </div>
              </div>
            );
          })()}

          {/* ── Google Ads per-campaign fraud estimates ── */}
          {!gadsLoading && gadsData && gadsData.has_data && gadsData.campaigns.length > 0 && (() => {
            const overallFraud = stats ? stats.fraud_clicks || 0 : 0;
            const overallTotal = stats ? stats.total_clicks || 1 : 1;
            const fraudRate = overallTotal > 0 ? overallFraud / overallTotal : 0;
            const maxClicks = Math.max(...gadsData.campaigns.map(c => c.clicks), 1);

            return (
              <>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="12" height="12" viewBox="0 0 48 48">
                    <path d="M33.6 43.2L43.2 26.4 33.6 9.6 24 26.4z" fill="#FBBC04"/>
                    <path d="M14.4 43.2L24 26.4 14.4 9.6 4.8 26.4z" fill="#4285F4"/>
                    <path d="M24 26.4L33.6 9.6H14.4L24 26.4z" fill="#34A853"/>
                  </svg>
                  Google Ads campaigns — Estimated fraud impact
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {(fraudRate * 100).toFixed(1)}% fraud rate</span>
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {gadsData.campaigns.map((c, i) => {
                    const clicks = Number(c.clicks) || 0;
                    const cost = Number(c.cost) || 0;
                    const avgCpc = Number(c.avg_cpc) || 0;
                    const effectiveCpc = clicks > 0 ? cost / clicks : avgCpc;
                    const estFraud = Math.round(clicks * fraudRate);
                    const estWasted = (estFraud * effectiveCpc).toFixed(2);
                    const fraudPct = clicks > 0 ? (estFraud / clicks) * 100 : 0;
                    const barW = maxClicks > 0 ? (clicks / maxClicks) * 100 : 0;
                    return (
                      <div key={i} style={{ padding: '7px 10px', background: 'var(--bg-subtle)', borderRadius: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--accent-red)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' }}>~{estFraud} fraud</span>
                          <span style={{ fontSize: 11, color: 'var(--accent-orange)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>~${estWasted}</span>
                        </div>
                        <div style={{ height: 4, borderRadius: 2, overflow: 'hidden', background: 'var(--bg-hover)', position: 'relative' }}>
                          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barW}%`, background: 'var(--accent-green)', opacity: 0.35 }} />
                          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barW * fraudPct / 100}%`, background: 'var(--accent-red)' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                          <span>{clicks.toLocaleString()} clicks · ${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spend</span>
                          <span style={{ color: fraudPct > 5 ? 'var(--accent-red)' : 'var(--text-muted)' }}>{fraudPct.toFixed(1)}% est. fraud</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {stats && stats.total_clicks === 0 && !(gadsData && gadsData.has_data) && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '20px 0', textAlign: 'center' }}>No campaign data yet.</div>
          )}

          {/* Legend */}
          {stats && stats.total_clicks > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              {Object.entries(FRAUD_TYPE_COLORS).map(([label, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row: Countries + Fraud Type */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        {/* Countries */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Fraudulent countries</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>Geographic fraud distribution</div>

          <MapWithTooltip topCountries={topCountries} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>1</span>
            <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'linear-gradient(to right, rgba(185,28,28,0.15), #b91c1c)' }} />
            <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text-muted)' }}>{topCountries[0]?.fraud || 0}</span>
          </div>

          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-blue)', marginBottom: 12 }}>Top 5 Fraudulent Countries</div>
          {topCountries.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No country data yet</div>
          ) : (
            <div>
              {topCountries.slice(0, 5).map((c, i) => {
                const genuine = c.total - c.fraud;
                const genuinePct = c.total > 0 ? (genuine / topCountries[0].total * 100) : 0;
                const fraudPct = c.total > 0 ? (c.fraud / topCountries[0].total * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-primary)', width: 100, textAlign: 'right', flexShrink: 0, fontWeight: 500 }}>{c.country}</span>
                    <div style={{ flex: 1, display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${genuinePct}%`, background: '#22c55e', minWidth: genuine > 0 ? 2 : 0 }} title={`Genuine: ${genuine}`} />
                      <div style={{ width: `${fraudPct}%`, background: '#ef4444', minWidth: c.fraud > 0 ? 2 : 0 }} title={`Fraud: ${c.fraud}`} />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <span style={{ width: 100 }} />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                  {[0, Math.round(topCountries[0]?.total * 0.25), Math.round(topCountries[0]?.total * 0.5), Math.round(topCountries[0]?.total * 0.75), topCountries[0]?.total || 0].map((v, i) => (
                    <span key={i} style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fraud Type Donut */}
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Fraud type</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>Breakdown by detection category</div>
          {fraudTypePie.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '30px 0', textAlign: 'center' }}>No fraud detected yet</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={fraudTypePie} cx="50%" cy="50%" innerRadius={75} outerRadius={115} dataKey="value" paddingAngle={2} strokeWidth={0}>
                    {fraudTypePie.map((d, i) => (
                      <Cell key={i} fill={FRAUD_TYPE_COLORS[d.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]}
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, justifyContent: 'center' }}>
                {FRAUD_TYPE_ORDER.map(label => {
                  const entry = fraudTypePie.find(d => d.name === label);
                  const hasData = entry && entry.value > 0;
                  return (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: hasData ? 'var(--bg-secondary)' : 'transparent', borderRadius: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: FRAUD_TYPE_COLORS[label], flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: hasData ? 'var(--text-primary)' : 'var(--text-muted)', textDecoration: !hasData ? 'line-through' : 'none', fontWeight: hasData ? 500 : 400 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
